'use client';

type ConsentRegion = 'gdpr' | 'ccpa' | 'none';

interface ConsentRecord {
  version: '1.0';
  timestamp: string;
  region: ConsentRegion;
  choices: {
    necessary: true;
    analytics: boolean;
    marketing: boolean;
    preferences: boolean;
  };
  userAgent: string;
}

const APP_NAME = 'buchhaltungaktas';
const CONSENT_KEY = APP_NAME.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_') + '_consent_v1';

/**
 * Detect user's region based on browser locale and timezone
 */
export function detectRegion(): ConsentRegion {
  if (typeof navigator === 'undefined') return 'gdpr';

  const lang = navigator.language || '';
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';

  const euTZPrefixes = [
    'Europe/',
    'Atlantic/Azores',
    'Atlantic/Canary',
    'Atlantic/Faroe',
    'Atlantic/Madeira',
    'Arctic/Longyearbyen',
  ];

  const euCountryCodes = [
    'AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI',
    'FR', 'GR', 'HR', 'HU', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT',
    'NL', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK', 'GB', 'IS', 'LI',
    'NO', 'CH',
  ];

  const isEU =
    euTZPrefixes.some((p) => tz.startsWith(p)) ||
    euCountryCodes.some((c) => lang.toUpperCase().endsWith('-' + c));

  if (isEU) return 'gdpr';
  return 'none';
}

/**
 * Get current consent from localStorage
 */
export function getConsent(): ConsentRecord | null {
  if (typeof window === 'undefined') return null;

  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('Error reading consent:', error);
    return null;
  }
}

/**
 * Save consent to localStorage
 */
export function saveConsent(choices: ConsentRecord['choices'], region: ConsentRegion): void {
  if (typeof window === 'undefined') return;

  try {
    const record: ConsentRecord = {
      version: '1.0',
      timestamp: new Date().toISOString(),
      region,
      choices: {
        necessary: true,
        ...choices,
      },
      userAgent: navigator.userAgent,
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(record));
  } catch (error) {
    console.error('Error saving consent:', error);
  }
}

/**
 * Clear consent from localStorage
 */
export function clearConsent(): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(CONSENT_KEY);
  } catch (error) {
    console.error('Error clearing consent:', error);
  }
}

/**
 * Check if a specific consent category is enabled
 */
export function isConsentGiven(category: keyof ConsentRecord['choices']): boolean {
  const consent = getConsent();
  if (!consent) return category === 'necessary';
  return consent.choices[category] ?? (category === 'necessary');
}
